import { until } from "@vueuse/core";
import axios, { AxiosHeaders } from "axios";
import { useAppInsights } from "@allwyn/utils/logger/app-insights";
import { ngFrameworkLoaded } from "../ng-integration";
import { useNgSession } from "../ng-session";

type GroupOperation = "assign-player" | "reassign-player" | "remove-player";
type GroupRequest = { gid: number } | { gids: number[] };

const httpClient = axios.create({
    baseURL: appSettings["Integrations:WebApi:BaseUrl"] ?? "",
});

async function updateGroups(operation: GroupOperation, request: GroupRequest): Promise<void> {
    try {
        const groupIds = "gids" in request ? request.gids : [request.gid];
        if (groupIds.some((id) => !Number.isInteger(id) || id <= 0)) {
            throw new Error("InvalidGroupId");
        }

        await ngFrameworkLoaded();
        const { session, isSessionStateFromCacheReady } = useNgSession();
        await until(isSessionStateFromCacheReady).toBe(true, { timeout: 10_000, throwOnTimeout: true });

        const playerId = session.value?.PlayerID;
        const sessionToken = session.value?.SessionToken;
        if (!playerId || !sessionToken) return;

        await httpClient.post(`/api/exponea/${operation}`, request, {
            headers: new AxiosHeaders().set("X-PLAYER-ID", playerId).set("X-SESSION-TOKEN", sessionToken),
        });
    } catch (error) {
        const status = axios.isAxiosError(error) ? error.response?.status : null;
        const reason = axios.isAxiosError<{ code?: unknown }>(error)
            ? error.response?.data?.code
            : error instanceof Error && error.message === "InvalidGroupId"
            ? "InvalidGroupId"
            : null;
        const knownReasons = [
            "SessionRejected",
            "MissingPlayerContext",
            "InvalidGroupId",
            "GroupNotAllowed",
            "InvalidRequest",
        ];

        useAppInsights().trackException(new Error("Exponea contact group update failed."), {
            operation,
            status: status ?? null,
            reason:
                status === 401
                    ? "SessionRejected"
                    : typeof reason === "string" && knownReasons.includes(reason)
                    ? reason
                    : "RequestFailed",
        });
    }
}

export function assignCurrentUserToGroup(groupId: number): Promise<void> {
    return updateGroups("assign-player", { gid: groupId });
}

export function reassignCurrentUserToGroups(...groupIds: number[]): Promise<void> {
    return updateGroups("reassign-player", { gids: groupIds });
}

export function removeCurrentUserFromGroup(groupId: number): Promise<void> {
    return updateGroups("remove-player", { gid: groupId });
}
